import { useTranslation } from 'react-i18next';

interface FinalCTAProps {
  /** Callback invoked when the CTA button is clicked */
  onContact: () => void;
}

/**
 * Final Call‑to‑Action component.
 *
 * Renders a title, description, contact button, email link and a list of
 * trust badges. The component is fully typed and includes defensive
 * handling for missing translation keys.
 */
export default function FinalCTA({ onContact }: FinalCTAProps) {
  const { t } = useTranslation();

  // Guard against undefined translations – fall back to an empty string
  const getSafeText = (key: string) => {
    const translation = t(key);
    return typeof translation === 'string' ? translation : '';
  };

  const trustBadges = [
    { icon: 'ri-time-line', text: getSafeText('cta.badge1') },
    { icon: 'ri-customer-service-2-line', text: getSafeText('cta.badge2') },
    { icon: 'ri-flask-line', text: getSafeText('cta.badge3') },
  ];

  return (
    <section id="contacto" className="py-14 sm:py-32 bg-[#03912c] relative overflow-hidden">
      <div className="max-w-4xl mx-auto px-5 sm:px-6 text-center relative z-10">
        <h2 className="text-[1.4rem] leading-tight sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-6">
          {getSafeText('cta.title')}
        </h2>
        <p className="text-sm sm:text-xl text-white/80 mb-5 sm:mb-10">
          {getSafeText('cta.desc')}
        </p>

        <button
          onClick={onContact}
          className="inline-flex items-center bg-[#d6df00] text-[#1A1A1A] px-5 sm:px-8 py-2.5 sm:py-4 rounded-full text-[0.95rem] sm:text-lg font-medium hover:bg-[#c2cb00] transition-all hover:scale-105 cursor-pointer whitespace-nowrap mb-3 sm:mb-4"
        >
          {getSafeText('cta.button')}
          <i className="ri-arrow-right-line ml-2 text-lg sm:text-xl"></i>
        </button>

        <p className="text-xs sm:text-base text-white/70 mb-6 sm:mb-10">
          {getSafeText('cta.email')}{' '}
          <a
            href="mailto:pppexportcostarica@gmail.com"
            className="underline hover:text-white cursor-pointer break-all"
          >
            pppexportcostarica@gmail.com
          </a>
        </p>

        <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-8 mt-6 sm:mt-12">
          {trustBadges.map((badge, index) => (
            <div
              key={index}
              className="flex items-center justify-center text-white/80"
            >
              <div className="w-5 h-5 flex items-center justify-center mr-2">
                <i className={`${badge.icon} text-lg sm:text-xl`}></i>
              </div>
              <span className="text-xs sm:text-sm">{badge.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
